import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Upload, X, Loader2, Search, Archive, FileText, Save, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { useSearchParams } from 'react-router-dom';
import { toast } from '../components/ui/sonner';
import { api, documentsAPI, tripsAPI, trucksAPI, driversAPI, operationsAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { tripOption, tripResume } from '../lib/selectOptions';
import { PageHeader } from '../components/ui/page-header';
import { SectionCard } from '../components/dashboard';
import { EmptyState } from '../components/ui/empty-state';
import { LEXIQUE } from '../lib/lexique';
import { nomLisible } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { useAuth } from '../context/AuthContext';
import CarteDocument from '../components/documents/CarteDocument';
import { AuthBlob, downloadAuthFile } from '../components/documents/fichierAuthentifie';
import {
  DEAD_END_STATES,
  formatDate,
  getOcrState,
  parseCustomLabel,
  typeDocument,
} from '../components/documents/lectureDocument';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { usePagination, usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { BarrePagination } from '../components/ui/pagination-bar';

// Catalogue des types de document proposés à l'ajout (les codes restent ceux
// du serveur; l'écran n'affiche que les libellés français).
const DOC_TYPES = [
  {
    value: 'RECEIPT',
    label: 'Reçu carburant',
    description: 'Ticket de station-service',
  },
  {
    value: 'INVOICE',
    label: 'Facture',
    description: 'Facture client ou fournisseur',
  },
  {
    value: 'CUSTOMS_DECLARATION',
    label: 'Déclaration douanière',
    description: 'Document de transit / douane',
  },
  {
    value: 'WAYBILL',
    label: 'Lettre de voiture',
    description: 'Bon de transport / livraison',
  },
  {
    value: 'IDENTITY_DOCUMENT',
    label: "Pièce d'identité",
    description: 'Carte ou passeport — numéro masqué, photo réservée à la direction',
  },
  {
    value: 'OTHER',
    label: 'Autre',
    description: 'Tout autre document',
  },
];

const ACCEPTED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

// Le pluriel se décide au dernier moment: « 1 document(s) » est le genre de
// détail qui fait douter du reste des chiffres affichés à côté.
const pluriel = (nombre, singulier, plurielForme) =>
  Number(nombre) > 1 ? plurielForme : singulier;

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const searchableText = (doc) => {
  const { label, base } = parseCustomLabel(doc.file_name);
  return [
    base,
    label,
    typeDocument(doc.doc_type),
    doc.file_type,
    doc.extracted_text,
    formatDate(doc.created_at),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

/** Aucun trajet: valeur du select, `null` n'étant pas une valeur de Select. */
const SANS_TRAJET = 'AUCUN';

// Les deux vues de la page, portées par l'adresse (`?vue=archives`): le retour
// arrière du navigateur et un lien partagé retombent sur la bonne.
const VUE_ARCHIVES = 'archives';

export default function DocumentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { canSeeMoney } = useAuth();
  const enArchives = searchParams.get('vue') === VUE_ARCHIVES;
  // Le dépôt n'envoyait jamais `associated_trip_id`. La pièce était bien
  // archivée, mais le trajet continuait de la réclamer: le seul chemin qui
  // débloquait quelque chose était l'application du chauffeur. Un lien
  // `/documents?trip=…` préremplit désormais le rattachement.
  const [linkedTripId, setLinkedTripId] = useState(
    () => searchParams.get('trip') || SANS_TRAJET,
  );
  const [linkableTrips, setLinkableTrips] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [operations, setOperations] = useState([]);
  const [tripFilter, setTripFilter] = useState('ALL');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [docType, setDocType] = useState('RECEIPT');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [ocrFilter, setOcrFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [manualDocId, setManualDocId] = useState(null);
  const [manualValues, setManualValues] = useState({});
  const [manualText, setManualText] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  // Archivage: confirmation avec un motif facultatif.
  const [archiveCible, setArchiveCible] = useState(null);
  const [archiveMotif, setArchiveMotif] = useState('');
  // Documents archivés: liste paginée par le serveur, et leur nombre total
  // (sans recherche) pour la pastille de la vue.
  const [archivedDocs, setArchivedDocs] = useState([]);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [archivedCount, setArchivedCount] = useState(null);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [unarchivingId, setUnarchivingId] = useState(null);
  const pollsLeft = useRef(0);
  const rechercheArchives = useDebouncedValue(searchQuery.trim());
  const paginationArchives = usePaginationServeur(archivedTotal, {
    cle: 'archives',
    reinitialiser: rechercheArchives,
  });
  const { page: pageArchives, limit: tailleArchives } = paginationArchives;

  const fetchDocuments = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setDocuments(await documentsAPI.getAll());
    } catch (error) {
      console.error('Failed to fetch documents', error);
      if (!silent) toast.error(messageErreur(error, 'Impossible de charger les documents'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Le nombre de documents archivés, pour la pastille de la vue: une page d'une
  // ligne suffit, le serveur renvoie le total.
  const fetchArchivedCount = useCallback(async () => {
    try {
      const { total } = await documentsAPI.listArchived({ page: 1, page_size: 1 });
      setArchivedCount(total);
    } catch (error) {
      // La pastille se passe de nombre; l'écran n'en dépend pas.
    }
  }, []);

  const nouvelleDemande = useDerniereDemande();
  const fetchArchived = useCallback(
    async ({ silent = false } = {}) => {
      const perimee = nouvelleDemande();
      try {
        if (!silent) setArchivedLoading(true);
        const params = { page: pageArchives, page_size: tailleArchives };
        if (rechercheArchives) params.q = rechercheArchives;
        const { items, total } = await documentsAPI.listArchived(params);
        if (perimee()) return;
        setArchivedDocs(items);
        setArchivedTotal(total);
        if (!rechercheArchives) setArchivedCount(total);
      } catch (error) {
        if (perimee()) return;
        console.error('Failed to fetch archived documents', error);
        if (!silent) toast.error(messageErreur(error, 'Impossible de charger les documents archivés'));
      } finally {
        if (!perimee()) setArchivedLoading(false);
      }
    },
    [pageArchives, tailleArchives, rechercheArchives, nouvelleDemande],
  );

  useEffect(() => {
    fetchArchivedCount();
  }, [fetchArchivedCount]);

  useEffect(() => {
    if (enArchives) fetchArchived();
  }, [enArchives, fetchArchived]);

  // Une pièce ajoutée depuis la fiche camion, un trajet ou un entretien, ou
  // dans un autre onglet, apparaît sans recharger la page.
  useRafraichirSur(['documents', 'camions', 'chauffeurs', 'maintenance', 'trajets'], () => {
    fetchDocuments({ silent: true });
    fetchArchivedCount();
    if (enArchives) fetchArchived({ silent: true });
  });

  const changerVue = (vue) => {
    setSearchParams(
      (precedent) => {
        const suivant = new URLSearchParams(precedent);
        if (vue === VUE_ARCHIVES) suivant.set('vue', VUE_ARCHIVES);
        else suivant.delete('vue');
        // Chaque vue pagine à part: on repart de sa première page.
        ['page', 'archives-page'].forEach((cle) => suivant.delete(cle));
        return suivant;
      },
      { replace: false },
    );
  };

  // Tous les trajets, camions, chauffeurs et convois: un trajet se reconnaît à
  // sa plaque, son itinéraire et sa date, et une pièce peut concerner un trajet
  // déjà clôturé. Chaque liste est chargée jusqu'à sa dernière page.
  useEffect(() => {
    let cancelled = false;
    const charger = (appel, appliquer) =>
      fetchAllPages(appel)
        .then(({ items }) => {
          if (!cancelled) appliquer(items);
        })
        .catch(() => {
          if (!cancelled) appliquer([]);
        });
    charger((params) => tripsAPI.getAllPaged(params), setLinkableTrips);
    charger((params) => trucksAPI.getAllPaged(params), setTrucks);
    charger((params) => driversAPI.getAllPaged(params), setDrivers);
    charger((params) => operationsAPI.getAllPaged(params), setOperations);
    return () => {
      cancelled = true;
    };
  }, []);

  const pendingCount = useMemo(
    () => documents.filter((doc) => getOcrState(doc).key === 'pending').length,
    [documents],
  );

  // La lecture tourne maintenant derrière la requête: sans ce rappel, la liste
  // resterait sur « lecture en cours » jusqu'à ce que l'utilisateur pense à
  // recharger la page. Le compteur borne l'attente — un serveur muet ne doit
  // pas faire boucler un téléphone en 3G indéfiniment.
  useEffect(() => {
    if (!pendingCount) {
      pollsLeft.current = 30;
      return undefined;
    }
    if (pollsLeft.current <= 0) return undefined;
    const timer = setTimeout(() => {
      pollsLeft.current -= 1;
      fetchDocuments({ silent: true });
    }, 4000);
    return () => clearTimeout(timer);
  }, [documents, fetchDocuments, pendingCount]);

  // Cleanup preview blob URL when file changes/unmounts to avoid leaks.
  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    if (!selectedFile.type.startsWith('image/')) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const validateAndSetFile = (file) => {
    if (!file) return;
    if (!ACCEPTED_MIMES.includes(file.type)) {
      toast.error('Format invalide. JPG, PNG, WEBP ou PDF uniquement.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Fichier trop volumineux (10 Mo maximum).');
      return;
    }
    setSelectedFile(file);
  };

  const onFileChange = (e) => {
    validateAndSetFile(e.target.files?.[0]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    validateAndSetFile(e.dataTransfer.files?.[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier.');
      return;
    }
    if (docType === 'OTHER' && !customLabel.trim()) {
      toast.error('Veuillez nommer ce type de document.');
      return;
    }

    setUploading(true);
    try {
      // Wrap the file with a custom label in its name when type is OTHER, so
      // the label survives in the listing without a backend schema change.
      let payloadFile = selectedFile;
      if (docType === 'OTHER' && customLabel.trim()) {
        const wrappedName = `[${customLabel.trim()}] ${selectedFile.name}`;
        payloadFile = new File([selectedFile], wrappedName, {
          type: selectedFile.type,
        });
      }

      // `documentsAPI.upload` sait poser les métadonnées; le FormData à la
      // main qui vivait ici ne portait que le fichier et son type, et perdait
      // donc le trajet à chaque fois.
      await documentsAPI.upload(payloadFile, docType, {
        associated_trip_id: linkedTripId === SANS_TRAJET ? null : linkedTripId,
        capture_source: 'web_operator',
      });
      toast.success(
        linkedTripId === SANS_TRAJET
          ? 'Document enregistré. La lecture automatique se termine en arrière-plan.'
          : 'Document enregistré et rattaché au trajet.',
      );
      setSelectedFile(null);
      document.getElementById('doc-file-input').value = '';
      setCustomLabel('');
      setDocType('RECEIPT');
      fetchDocuments();
    } catch (error) {
      console.error(error);
      toast.error(messageErreur(error, 'Échec du téléversement'));
    } finally {
      setUploading(false);
    }
  };

  const retryOcr = async (id) => {
    try {
      await documentsAPI.retryOcr(id);
      await fetchDocuments();
      toast.success('Nouvelle lecture lancée en arrière-plan');
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de relancer la lecture'));
    }
  };

  const openManualEntry = (doc) => {
    if (manualDocId === doc.id) {
      setManualDocId(null);
      return;
    }
    // Pré-remplir avec ce qui a déjà été lu évite de retaper les champs
    // corrects et concentre l'utilisateur sur ce qui manque vraiment.
    const known = doc.ocr_fields || {};
    setManualValues(
      (doc.ocr_manual_fields || []).reduce((acc, field) => {
        const value = known[field.key];
        acc[field.key] = value === null || value === undefined ? '' : String(value);
        return acc;
      }, {}),
    );
    setManualText('');
    setManualDocId(doc.id);
  };

  const submitManualEntry = async (doc) => {
    const fields = Object.entries(manualValues).reduce((acc, [key, value]) => {
      if (String(value).trim() !== '') acc[key] = String(value).trim();
      return acc;
    }, {});
    if (!Object.keys(fields).length && !manualText.trim()) {
      toast.error('Renseignez au moins un champ ou le texte du document.');
      return;
    }
    setSavingManual(true);
    try {
      await api.patch(`/documents/${doc.id}/fields`, {
        fields,
        extracted_text: manualText.trim() || null,
      });
      setManualDocId(null);
      await fetchDocuments();
      toast.success('Informations enregistrées et tracées comme saisie manuelle');
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible d’enregistrer la saisie'));
    } finally {
      setSavingManual(false);
    }
  };

  // Archiver demande confirmation, avec un motif facultatif gardé sur le document.
  const demanderArchivage = (doc) => {
    setArchiveMotif('');
    setArchiveCible(doc);
  };

  const archiveDocument = async () => {
    const doc = archiveCible;
    if (!doc) return;
    setArchivingId(doc.id);
    try {
      await documentsAPI.archive(doc, archiveMotif.trim() || undefined);
      setArchiveCible(null);
      setDocuments((rows) => rows.filter((row) => row.id !== doc.id));
      await fetchDocuments();
      fetchArchivedCount();
      toast.success('Document archivé');
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible d’archiver le document'));
    } finally {
      setArchivingId(null);
    }
  };

  // Désarchiver remet le document dans les listes (et sur son trajet): les deux
  // vues se rechargent.
  const unarchiveDocument = async (doc) => {
    setUnarchivingId(doc.id);
    try {
      await documentsAPI.unarchive(doc);
      toast.success('Document désarchivé');
      await Promise.all([
        fetchDocuments({ silent: true }),
        fetchArchived({ silent: true }),
        fetchArchivedCount(),
      ]);
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de désarchiver le document'));
    } finally {
      setUnarchivingId(null);
    }
  };

  const reinitialiserFiltres = () => {
    setSearchQuery('');
    setTypeFilter('ALL');
    setOcrFilter('ALL');
    setTripFilter('ALL');
  };

  const contexteTrajet = useMemo(() => ({ trucks, drivers, operations }), [trucks, drivers, operations]);
  const optionsTrajets = useMemo(
    () => linkableTrips.map((trip) => tripOption(trip, contexteTrajet)),
    [linkableTrips, contexteTrajet],
  );
  const tripsParId = useMemo(() => new Map(linkableTrips.map((trip) => [trip.id, trip])), [linkableTrips]);
  const resumeTrajet = useCallback(
    (tripId) => {
      const trip = tripsParId.get(tripId);
      return trip ? tripResume(trip, contexteTrajet) : null;
    },
    [tripsParId, contexteTrajet],
  );

  const tripOptions = useMemo(
    () => [
      { value: SANS_TRAJET, label: 'Aucun trajet' },
      ...optionsTrajets,
      ...(linkedTripId !== SANS_TRAJET && !tripsParId.has(linkedTripId)
        ? [{ value: linkedTripId, label: 'Trajet sélectionné' }]
        : []),
    ],
    [optionsTrajets, linkedTripId, tripsParId],
  );

  // Filtre: seulement les trajets qui portent au moins un document.
  const optionsFiltreTrajet = useMemo(() => {
    const avecDocuments = new Set(documents.map((doc) => doc.associated_trip_id).filter(Boolean));
    return [
      { value: 'ALL', label: 'Tous les trajets' },
      ...optionsTrajets.filter((option) => avecDocuments.has(option.value)),
    ];
  }, [documents, optionsTrajets]);

  const sortedDocuments = useMemo(
    () =>
      [...documents].sort((a, b) =>
        String(b.created_at || '').localeCompare(String(a.created_at || '')),
      ),
    [documents],
  );

  const filteredDocuments = useMemo(
    () =>
      sortedDocuments.filter((doc) => {
        const typeOk = typeFilter === 'ALL' || doc.doc_type === typeFilter;
        const tripOk = tripFilter === 'ALL' || doc.associated_trip_id === tripFilter;
        const stateKey = getOcrState(doc).key;
        // « Échec » recouvre trois clés serveur (PDF illisible, fichier
        // manquant, échec moteur): les distinguer dans le filtre cacherait
        // justement les documents qui demandent une action.
        const ocrOk =
          ocrFilter === 'ALL' ||
          (ocrFilter === 'failed' ? DEAD_END_STATES.has(stateKey) : stateKey === ocrFilter);
        const query = searchQuery.trim().toLowerCase();
        const texteTrajet = (resumeTrajet(doc.associated_trip_id) || '').toLowerCase();
        const searchOk =
          !query ||
          query.split(/\s+/).every((part) => `${searchableText(doc)} ${texteTrajet}`.includes(part));
        return typeOk && ocrOk && tripOk && searchOk;
      }),
    [ocrFilter, resumeTrajet, searchQuery, sortedDocuments, tripFilter, typeFilter],
  );
  const pagination = usePagination(filteredDocuments, {
    reinitialiser: `${searchQuery}|${typeFilter}|${tripFilter}|${ocrFilter}`,
  });

  const stats = useMemo(() => {
    const total = documents.length;
    const keys = documents.map((doc) => getOcrState(doc).key);
    // Un document saisi à la main est exploitable: le compter avec les
    // lectures fiables, sinon l'écran donne l'impression que le travail de
    // reprise n'a servi à rien.
    const exploitable = keys.filter((key) => key === 'reliable' || key === 'manual').length;
    const pending = keys.filter((key) => key === 'pending').length;
    const toFix = keys.filter((key) => DEAD_END_STATES.has(key) || key === 'review').length;
    const byType = documents.reduce((acc, d) => {
      acc[d.doc_type] = (acc[d.doc_type] || 0) + 1;
      return acc;
    }, {});
    return { total, exploitable, pending, toFix, byType };
  }, [documents]);

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        // Le sourcil nommait le module et le titre nommait le même module: le
        // même mot deux fois, l'un au-dessus de l'autre.
        title="Documents"
        stats={
          loading
            ? undefined
            : [
                { label: pluriel(stats.total, 'document', 'documents'), valeur: stats.total },
                { label: 'exploitables', valeur: stats.exploitable, ton: 'positif' },
                { label: 'en cours de lecture', valeur: stats.pending },
                {
                  label: 'à compléter',
                  valeur: stats.toFix,
                  ton: stats.toFix > 0 ? 'critique' : 'neutre',
                },
              ]
        }
        description="Reçus, factures, déclarations douanières et lettres de voiture. Leur texte est lu automatiquement, ce qui permet de les retrouver par leur contenu."
        actions={
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un document…"
              aria-label={enArchives ? 'Rechercher dans les documents archivés' : 'Rechercher dans tous les documents'}
              className="pl-9"
            />
          </div>
        }
      />

      <SectionCard icon={Upload} title="Ajouter un document">
        <form onSubmit={handleUpload} className="grid items-end gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1.3fr_1.5fr_auto]">
          <div>
            <Label htmlFor="document-type" className="mb-1.5 block">Type de document</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger id="document-type"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="document-trip" className="mb-1.5 block">Trajet concerné</Label>
            <SearchableSelect
              id="document-trip"
              data-testid="document-trip-select"
              placeholder="Aucun trajet"
              searchPlaceholder="Rechercher un trajet…"
              emptyMessage="Aucun trajet trouvé"
              value={linkedTripId || ''}
              onValueChange={setLinkedTripId}
              options={tripOptions}
            />
          </div>
          <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} className={dragOver ? 'rounded-md ring-2 ring-primary' : ''}>
            <Label htmlFor="doc-file-input" className="mb-1.5 block">Fichier · JPG, PNG, WEBP, PDF · 10 Mo max</Label>
            <Input id="doc-file-input" type="file" accept={ACCEPTED_MIMES.join(',')} onChange={onFileChange} disabled={uploading} className="cursor-pointer" />
          </div>
          <Button type="submit" disabled={!selectedFile || uploading} className="gap-2">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Envoi en cours…' : 'Ajouter'}
          </Button>
          {docType === 'OTHER' && <div className="md:col-span-2">
            <Label htmlFor="document-custom-label" className="mb-1.5 block">Nom du type</Label>
            <Input id="document-custom-label" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} placeholder="Ex. Assurance" maxLength={50} />
          </div>}
          {selectedFile && <div className="flex min-w-0 items-center gap-3 md:col-span-2 xl:col-span-4">
            {previewUrl && <img src={previewUrl} alt="Fichier sélectionné" className="h-10 w-10 rounded object-cover" />}
            <span className="truncate text-sm">{selectedFile.name} · {formatBytes(selectedFile.size)}</span>
            <Button type="button" variant="ghost" size="icon" aria-label="Retirer le fichier" disabled={uploading} onClick={() => { setSelectedFile(null); document.getElementById('doc-file-input').value = ''; }}><X className="h-4 w-4" /></Button>
          </div>}
        </form>
      </SectionCard>

      <div>
        <section className="rounded-2xl border border-border/70 bg-card shadow-sm">
          <header className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 md:px-5 md:pt-5 xl:flex-nowrap">
            {/* Deux vues, une seule liste à l'écran: les documents en service,
                et ceux qu'on a retirés des listes (archivés). */}
            <div
              role="group"
              aria-label="Vue des documents"
              data-testid="documents-vues"
              className="flex min-w-0 items-center gap-0.5 rounded-full border border-border bg-muted p-0.5"
            >
              {[
                { vue: 'documents', libelle: 'Documents', Icone: FileText, actif: !enArchives },
                { vue: VUE_ARCHIVES, libelle: 'Archivés', Icone: Archive, actif: enArchives },
              ].map(({ vue, libelle, Icone, actif }) => (
                <button
                  key={vue}
                  type="button"
                  aria-pressed={actif}
                  onClick={() => changerVue(vue)}
                  className={
                    'inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
                    (actif
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground')
                  }
                >
                  <Icone className="h-4 w-4" aria-hidden="true" />
                  {libelle}
                  {vue === VUE_ARCHIVES && archivedCount != null ? (
                    <span
                      data-testid="documents-archives-nombre"
                      className="rounded-full bg-primary/10 px-1.5 text-xs tabular-nums text-primary"
                    >
                      {archivedCount}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
            {/* Filtres dans l'en-tête, à droite du titre: une rangée de moins
                avant le premier document. Les archivés se cherchent par le
                champ de recherche de la page. */}
            {!enArchives && (
              <div className="flex w-full flex-wrap items-center gap-2 xl:w-auto xl:max-w-[72%] xl:flex-none xl:justify-end">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-44" aria-label="Filtrer par type"><SelectValue placeholder="Tous les types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tous les types</SelectItem>
                    {DOC_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={ocrFilter} onValueChange={setOcrFilter}>
                  <SelectTrigger className="w-full sm:w-44" aria-label="Filtrer par lecture"><SelectValue placeholder="Toutes les lectures" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Toutes les lectures</SelectItem>
                    <SelectItem value="reliable">{LEXIQUE.lectureFiable}</SelectItem>
                    <SelectItem value="review">{LEXIQUE.lectureACompleter}</SelectItem>
                    <SelectItem value="failed">{LEXIQUE.lectureImpossible}</SelectItem>
                  </SelectContent>
                </Select>
                <SearchableSelect
                  className="w-full sm:w-56"
                  aria-label="Filtrer par trajet"
                  data-testid="document-trip-filter"
                  placeholder="Tous les trajets"
                  searchPlaceholder="Rechercher un trajet…"
                  emptyMessage="Aucun trajet trouvé"
                  value={tripFilter}
                  onValueChange={setTripFilter}
                  options={optionsFiltreTrajet}
                />
                <Button type="button" variant="outline" onClick={reinitialiserFiltres}>
                  Réinitialiser
                </Button>
              </div>
            )}
            {enArchives ? (
              !archivedLoading && (
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                  {archivedTotal} {pluriel(archivedTotal, 'document', 'documents')}
                </span>
              )
            ) : (
              !loading && (
                <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                  {filteredDocuments.length}{' '}
                  {pluriel(filteredDocuments.length, 'document', 'documents')}
                </span>
              )
            )}
          </header>

          <div className="px-2 pb-3 pt-3 sm:px-4 sm:pb-4 sm:pt-4 md:px-5 md:pb-5">

            {enArchives ? (
              archivedLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
                </div>
              ) : archivedDocs.length === 0 ? (
                rechercheArchives ? (
                  <EmptyState
                    variant="filtered"
                    title="Aucun document archivé ne correspond"
                    actionLabel="Effacer la recherche"
                    onAction={() => setSearchQuery('')}
                  />
                ) : (
                  <EmptyState illustration="donnee" title="Aucun document archivé" />
                )
              ) : (
                <div className="space-y-4" data-testid="documents-archives">
                  {archivedDocs.map((doc) => {
                    const { base: baseFileName } = parseCustomLabel(doc.file_name);
                    return (
                      <CarteDocument
                        key={doc.id}
                        doc={doc}
                        archive
                        resumeTrajet={resumeTrajet}
                        canSeeMoney={canSeeMoney}
                        unarchiving={unarchivingId === doc.id}
                        onPreview={setPreviewDocument}
                        onDownload={() => downloadAuthFile(doc.url, baseFileName)}
                        onUnarchive={unarchiveDocument}
                      />
                    );
                  })}
                </div>
              )
            ) : loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
              </div>
            ) : documents.length === 0 ? (
              // Rien n'existe encore: c'est une page à remplir, pas un filtre
              // à corriger. L'illustration tient la place que le vide laisse.
              <EmptyState
                illustration="donnee"
                title="Aucun document"
                description="Déposez un reçu, une facture, une déclaration douanière ou une lettre de voiture : le fichier est enregistré immédiatement et la lecture du texte suit en arrière-plan."
              />
            ) : filteredDocuments.length === 0 ? (
              // Des documents existent mais aucun ne passe le filtre: l'action
              // utile n'est pas « téléverser », c'est « rouvrir la vue ».
              <EmptyState
                variant="filtered"
                title="Aucun document ne correspond"
                description="Aucun document ne répond à cette recherche ni à ces filtres."
                actionLabel="Réinitialiser les filtres"
                onAction={reinitialiserFiltres}
              />
            ) : (
              <div className="space-y-4">
                {pagination.lignes.map((doc) => {
                  const manualOpen = manualDocId === doc.id;
                  const manualFields = doc.ocr_manual_fields || [];
                  const { base: baseFileName } = parseCustomLabel(doc.file_name);
                  return (
                    <CarteDocument
                      key={doc.id}
                      doc={doc}
                      resumeTrajet={resumeTrajet}
                      canSeeMoney={canSeeMoney}
                      manualOpen={manualOpen}
                      archiving={archivingId === doc.id}
                      onPreview={setPreviewDocument}
                      onDownload={() => downloadAuthFile(doc.url, baseFileName)}
                      onArchive={demanderArchivage}
                      onManualEntry={openManualEntry}
                      onRetry={() => retryOcr(doc.id)}
                      footer={
                        doc.manual_entry?.by ? (
                          <p className="mt-2 text-[11px] text-muted-foreground">
                            Saisi par {nomLisible(doc.manual_entry.by)} le {formatDate(doc.manual_entry.at)}
                          </p>
                        ) : null
                      }
                    >
                      {manualOpen && (
                        <div className="mt-3 space-y-3 rounded-lg border border-border bg-muted/30 p-3">
                          <p className="text-xs font-semibold text-foreground">
                            Saisie manuelle — {typeDocument(doc.doc_type)}
                          </p>
                          <div className="grid gap-2 sm:grid-cols-2">
                            {manualFields.map((field) => (
                              <div key={field.key}>
                                <Label className="mb-1 block text-xs text-muted-foreground">
                                  {field.label}
                                  {field.required && <span className="text-destructive"> *</span>}
                                </Label>
                                <Input
                                  type="text"
                                  inputMode={field.type === 'number' ? 'decimal' : undefined}
                                  placeholder={field.placeholder || ''}
                                  value={manualValues[field.key] ?? ''}
                                  onChange={(e) =>
                                    setManualValues((prev) => ({
                                      ...prev,
                                      [field.key]: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                            ))}
                          </div>
                          <div>
                            <Label className="mb-1 block text-xs text-muted-foreground">
                              Texte du document (facultatif, rend la pièce recherchable)
                            </Label>
                            <Textarea
                              rows={3}
                              value={manualText}
                              onChange={(e) => setManualText(e.target.value)}
                              placeholder="Recopiez les mentions utiles: client, destination, mentions manuscrites…"
                            />
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              className="gap-1"
                              disabled={savingManual}
                              onClick={() => submitManualEntry(doc)}
                            >
                              {savingManual ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <Save className="h-3.5 w-3.5" />
                              )}
                              Enregistrer
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setManualDocId(null)}
                            >
                              Annuler
                            </Button>
                            <Aide titre="Saisie manuelle">
                              Ce qui est saisi ici est enregistré comme saisie humaine, datée et
                              signée par votre compte.
                            </Aide>
                          </div>
                        </div>
                      )}
                    </CarteDocument>
                  );
                })}
              </div>
            )}
            {enArchives ? (
              !archivedLoading && archivedDocs.length > 0 ? (
                <BarrePagination pagination={paginationArchives} nom="documents archivés" />
              ) : null
            ) : !loading && filteredDocuments.length > 0 ? (
              <BarrePagination pagination={pagination} nom="documents" />
            ) : null}
          </div>
        </section>
      </div>
      <Dialog
        open={Boolean(archiveCible)}
        onOpenChange={(open) => {
          if (!open && !archivingId) setArchiveCible(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="break-words">
              Archiver « {archiveCible ? parseCustomLabel(archiveCible.file_name).base : ''} » ?
            </DialogTitle>
            <DialogDescription>Le fichier n’est pas supprimé : il reste dans Archivés.</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="archive-motif" className="mb-1.5 block">Motif (facultatif)</Label>
            <Input
              id="archive-motif"
              value={archiveMotif}
              onChange={(e) => setArchiveMotif(e.target.value)}
              placeholder="Ex. Doublon, mauvais camion"
              maxLength={500}
              disabled={Boolean(archivingId)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={Boolean(archivingId)} onClick={() => setArchiveCible(null)}>
              Annuler
            </Button>
            <Button type="button" className="gap-1.5" disabled={Boolean(archivingId)} onClick={archiveDocument}>
              {archivingId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
              Archiver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(previewDocument)} onOpenChange={(open) => { if (!open) setPreviewDocument(null); }}>
        <DialogContent className="flex max-h-[92vh] w-[calc(100vw_-_2rem)] max-w-5xl flex-col overflow-hidden bg-card">
          <DialogHeader className="shrink-0 pr-6">
            <DialogTitle className="break-words">{previewDocument ? parseCustomLabel(previewDocument.file_name).base : 'Aperçu du document'}</DialogTitle>
            <DialogDescription>{previewDocument ? typeDocument(previewDocument.doc_type) : 'Document'} · {formatDate(previewDocument?.created_at)}</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 overflow-y-auto rounded-lg border bg-muted/20 p-3">
            {previewDocument?.url ? <AuthBlob key={previewDocument.id} url={previewDocument.url}
              fallback={<p role="status" className="flex items-center justify-center gap-2 p-10"><Loader2 className="h-5 w-5 animate-spin" />Chargement de l’aperçu…</p>}
              render={(url) => (previewDocument.file_type || '').startsWith('image/')
                ? <img src={url} alt={parseCustomLabel(previewDocument.file_name).base} className="h-auto w-full rounded object-contain" />
                : <iframe src={url} title="Aperçu du document PDF" className="h-[65vh] w-full rounded bg-white" />}
            /> : <p className="p-6 text-sm">Fichier indisponible.</p>}
            {previewDocument?.extracted_text && <section className="mt-4 rounded-lg bg-card p-4"><h3 className="mb-2 font-semibold">Texte extrait</h3><p className="whitespace-pre-wrap break-words text-sm">{previewDocument.extracted_text}</p></section>}
          </div>
          {previewDocument?.url && <Button variant="outline" className="shrink-0 self-end" onClick={() => downloadAuthFile(previewDocument.url, parseCustomLabel(previewDocument.file_name).base)}><Download className="mr-2 h-4 w-4" />Télécharger</Button>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
